import { InitOptionsJSON } from '@acsbe/widget-settings';
import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { LicenseDetails } from '../pages/root/subscription/types';
import { AccessibeAccount } from '../type/AccessibeAccount';
import { CMSFeedbackTriggerType } from '../type/UserFeedback';
import { CMS } from '../utils/constants';
import { Logger } from '../utils/logger';
import { CustomInstallationConfig } from '../validations';
import axios from './../utils/axiosInstance';
import { useMerchantSession } from './MerchantSessionContext';
import { CmsDomain, DomainVisitCountReport } from './types';
import { repairWidgetConfig } from './widgetConfigValidator';

interface DomainSelectionProviderProps {
  children: ReactNode;
}

enum Roles {
  OWNER = 'owner',
  PARTNER = 'partner',
  MEMBER = 'member',
}

const DomainSelectionContext = createContext<
  | {
      accountId: string | undefined;
      setAccountId: Dispatch<SetStateAction<string | undefined>>;
      domain: string | undefined;
      setDomain: Dispatch<SetStateAction<string | undefined>>;
      domainsFetched: boolean;
      setDomainsFetched: Dispatch<SetStateAction<boolean>>;
      licenseId: string | undefined;
      setLicenseId: (licenseId: string | undefined) => void;
      licenseDetails: LicenseDetails | undefined;
      setLicenseDetails: Dispatch<SetStateAction<LicenseDetails | undefined>>;
      paymentOverlayMessage: string;
      setPaymentOverlayMessage: Dispatch<SetStateAction<string>>;
      refreshLicenseDetails: () => void;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      widgetSettings: any | undefined;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      updateWidgetSettings: (widgetConfig: any) => Promise<void>;
      isWidgetConfigDefault: boolean;
      currentAccessibeAccount: AccessibeAccount | null;
      setCurrentAccessibeAccount: React.Dispatch<
        React.SetStateAction<AccessibeAccount | null>
      >;
      allDomains: Array<CmsDomain>;
      setAllDomains: Dispatch<SetStateAction<Array<CmsDomain>>>;
      visitCountReport: DomainVisitCountReport | undefined;
      setVisitCountReport: Dispatch<
        SetStateAction<DomainVisitCountReport | undefined>
      >;
      changedConfig: CustomInstallationConfig | null;
      setChangedConfig: (config: CustomInstallationConfig | null) => void;
      showDiscardModal: boolean;
      setShowDiscardModal: (show: boolean) => void;
      postFeedback: () => Promise<void>;
      visitCountFetched: boolean;
      setVisitCountFetched: Dispatch<SetStateAction<boolean>>;
      isCustomDiscount: boolean;
      setIsCustomDiscount: Dispatch<SetStateAction<boolean>>;
      noEmailSignupDomain: string | null;
      setNoEmailSignupDomain: Dispatch<SetStateAction<string | null>>;
    }
  | undefined
>(undefined);

const DomainSelectionProvider: React.FC<DomainSelectionProviderProps> = ({
  children,
}) => {
  const [accountId, setAccountId] = useState<string>();
  const [domain, setDomain] = useState<string>();
  const [licenseId, _setLicenseId] = useState<string>();
  const [licenseDetails, setLicenseDetails] = useState<LicenseDetails>();
  const [allDomains, setAllDomains] = useState<Array<CmsDomain>>([]);
  const [domainsFetched, setDomainsFetched] = useState<boolean>(false);
  const [visitCountFetched, setVisitCountFetched] = useState<boolean>(false);
  const [_refreshLicenseDetails, _setRefreshLicenseDetails] =
    useState<number>(0);
  const [paymentOverlayMessage, setPaymentOverlayMessage] =
    useState<string>('');
  const [currentAccessibeAccount, setCurrentAccessibeAccount] =
    useState<AccessibeAccount | null>(null);

  const [visitCountReport, setVisitCountReport] = useState<
    DomainVisitCountReport | undefined
  >(undefined);

  const [isCustomDiscount, setIsCustomDiscount] = useState<boolean>(false);
  const [noEmailSignupDomain, setNoEmailSignupDomain] = useState<string | null>(
    null,
  );

  const {
    merchantDetails,
    feedbackVisible,
    setFeedbackVisible,
    userFeedback,
    setUserFeedback,
    accessibeUser,
  } = useMerchantSession();

  const setLicenseId = (licenseId: string | undefined) => {
    Logger.log('Setting licenseId in context', { licenseId });
    _setLicenseId(licenseId);
    window.localStorage.setItem('selectedLicenseId', licenseId || '');
  };

  useEffect(() => {
    const licenseId = window.localStorage.getItem('selectedLicenseId');
    if (licenseId) {
      _setLicenseId(licenseId);
    }
  }, [_setLicenseId]);

  const refreshLicenseDetails = useCallback(() => {
    _setRefreshLicenseDetails(_refreshLicenseDetails + 1);
  }, [_refreshLicenseDetails]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [widgetSettings, _setWidgetSettings] = useState<any>();
  const [isWidgetConfigDefault, setIsWidgetConfigDefault] =
    useState<boolean>(true);

  const _fetchWidgetConfig = async (domain: string) => {
    Logger.debug('Fetching widget config for domain', domain);
    const response = await axios.get(
      `/api/accessibe/widget-config?domain=${domain}`,
    );
    setIsWidgetConfigDefault(response.data.isDefault);
    _setWidgetSettings(response.data.settings);
  };

  const _updateWidgetSettings = async (
    domain: string,
    licenseId: string,
    widgetSettings: Partial<InitOptionsJSON>,
  ) => {
    Logger.debug('Updating widget config for domain', domain);
    if (!domain || !licenseId || !widgetSettings) {
      Logger.log('licenseId or widgetSettings is missing', {
        domain,
        licenseId,
        widgetSettings,
      });
      return;
    }
    const _widgetSettings = repairWidgetConfig(widgetSettings, {
      domain: domain,
    });
    const response = await axios.put(`/api/accessibe/widget-config`, {
      licenseId,
      domain,
      widgetSettings: _widgetSettings,
    });

    if (response.status === 200) {
      Logger.debug('Widget config updated successfully', response.data);
      _fetchWidgetConfig(domain);
    }
  };

  useEffect(() => {
    if (domain) {
      _fetchWidgetConfig(domain);
    }
  }, [domain]);

  useEffect(() => {
    const fetchVisitCounts = async () => {
      try {
        if (!licenseId) {
          return;
        }
        const email =
          merchantDetails?.email ||
          merchantDetails?.nativeCmsUserDetails?.email ||
          accessibeUser?.email;

        const response = await axios.post(
          `/api/accessibe/license-visit-counts`,
          { licenseId: licenseId, email },
          {
            withCredentials: true,
          },
        );
        if (response.status === 200) {
          setVisitCountReport(response.data.data);
        }
      } catch (error) {
        setVisitCountReport({
          averageMonthlyTraffic: 0,
          numberOfMonths: 0,
          validDomain: false,
        });
        Logger.error('Error fetching Accessibe account', error);
      }
    };

    const license = async () => {
      if (licenseId !== undefined && licenseId !== '') {
        const response = await axios.post(
          '/api/accessibe/license-details',
          { licenseId: licenseId },
          {
            withCredentials: true,
          },
        );
        setAccountId(response.data.data.accountId);
        setDomain(response.data.data.domain);
        setLicenseDetails(response.data.data);
      }
    };
    license();
    if (!visitCountFetched) {
      fetchVisitCounts();
    }
  }, [
    licenseId,
    visitCountFetched,
    _refreshLicenseDetails,
    setAccountId,
    setDomain,
    setLicenseDetails,
    merchantDetails?.email,
    merchantDetails?.nativeCmsUserDetails?.email,
    accessibeUser?.email,
  ]);

  useEffect(() => {
    const getFeedback = async () => {
      if (licenseId && merchantDetails?.source && domain) {
        try {
          const response = await axios.get(
            `/api/feedback/get-feedback?pluginInstallSource=${merchantDetails?.source}`,
            {
              withCredentials: true,
            },
          );
          const feedbacks = response?.data?.data?.feedbacks;
          if (!feedbacks || feedbacks.length === 0) {
            setFeedbackVisible(true);
          }
          if (response?.data?.data?.wixPostPurchase) {
            setUserFeedback({
              cmsFeedbackTriggerType: CMSFeedbackTriggerType.POST_PURCHASE,
              pluginInstallSource: merchantDetails?.source as CMS,
              siteUrl: domain,
              wixPostPurchase: false,
            });
          }
        } catch (error) {
          Logger.error('Error fetching user feedback', error);
        }
      }
    };
    getFeedback();
  }, [
    merchantDetails?.source,
    licenseId,
    domain,
    setFeedbackVisible,
    setUserFeedback,
  ]);

  const postFeedback = useCallback(async () => {
    setFeedbackVisible(false);
    try {
      await axios.post(
        '/api/feedback/post-feedback',
        {
          userId: accessibeUser?.userId,
          ...userFeedback,
        },
        {
          withCredentials: true,
        },
      );
      setUserFeedback(null);
    } catch (error) {
      Logger.error('Error posting user feedback', error);
    }
  }, [
    userFeedback,
    setFeedbackVisible,
    setUserFeedback,
    accessibeUser?.userId,
  ]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (feedbackVisible) {
        postFeedback();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedbackVisible]);

  const updateWidgetSettings = React.useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (widgetSettings: any) => {
      if (licenseId && domain) {
        await _updateWidgetSettings(domain, licenseId, widgetSettings);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [licenseId, domain],
  );

  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [changedConfig, setChangedConfig] =
    useState<CustomInstallationConfig | null>(null);

  return (
    <DomainSelectionContext.Provider
      value={{
        accountId,
        setAccountId,
        domain,
        setDomain,
        licenseId,
        setLicenseId,
        licenseDetails,
        setLicenseDetails,
        paymentOverlayMessage,
        setPaymentOverlayMessage,
        refreshLicenseDetails,
        widgetSettings,
        updateWidgetSettings,
        isWidgetConfigDefault,
        currentAccessibeAccount,
        setCurrentAccessibeAccount,
        allDomains,
        setAllDomains,
        domainsFetched,
        setDomainsFetched,
        visitCountReport,
        setVisitCountReport,
        changedConfig,
        setChangedConfig,
        showDiscardModal,
        setShowDiscardModal,
        postFeedback,
        visitCountFetched,
        setVisitCountFetched,
        isCustomDiscount,
        setIsCustomDiscount,
        noEmailSignupDomain,
        setNoEmailSignupDomain,
      }}
    >
      {children}
    </DomainSelectionContext.Provider>
  );
};

const useDomainSelection = () => {
  const context = useContext(DomainSelectionContext);
  if (context === undefined) {
    throw new Error(
      'useDomainSelection must be used within a SelectedDomainProvider',
    );
  }
  return context;
};

export { DomainSelectionProvider, useDomainSelection, Roles };
